<template>
  <BaseDialog
    :is-open="isOpen"
    shrink
    :is-loading="isLoading"
    @close="emit('close')"
  >
    <div class="p-4">
      <template v-if="step === 'initial'">
        <div class="flex-1 overflow-y-auto">
          <section class="mb-2">
            <div
              class="flex items-center justify-center text-lg font-semibold"
            >
              <template
                v-if="transaction.status === 'successful'"
              >
                <span> {{ typeLabel }} Successful </span>
                <IconHeroiconsCheckCircle
                  class="text-positive ml-2"
                />
              </template>
              <template
                v-else-if="transaction.status === 'failed'"
              >
                <span> {{ typeLabel }} Failed </span>
                <IconHeroiconsXCircle
                  class="text-error ml-2"
                />
              </template>
              <template v-else>
                <span
                  >{{ typeLabel }} {{ statusLabel }}</span
                >
                <IconHeroiconsExclamationCircle
                  class="text-warning ml-2"
                />
              </template>
            </div>
          </section>
          <section>
            <ActionItemSmall
              label="Download Merchant Receipt"
              :icon="PrintReceiptIcon"
              @click="onDownloadMerchantReceipt"
            />

            <ActionItemSmall
              label="Email Customer Receipt"
              :icon="EmailReceiptIcon"
              @click="step = 'emailReceipt'"
            />
            <ActionItemSmall
              v-if="organizationConfig?.smsServiceEnabled"
              label="SMS Customer Receipt"
              :icon="SMSReceiptIcon"
              @click="step = 'smsReceipt'"
            />
          </section>
        </div>
      </template>

      <div
        v-else-if="step === 'emailReceipt'"
        class="px-3 pb-4"
      >
        <DialogButtonClose
          class="mb-4"
          @click="step = 'initial'"
        />
        <ReceiptEmailForm
          :object-id="transaction.id"
          receipt-type="transaction"
          @submit-success="emit('close')"
        />
      </div>

      <div
        v-else-if="step === 'smsReceipt'"
        class="px-3 pb-4"
      >
        <DialogButtonClose
          class="mb-4"
          @click="step = 'initial'"
        />
        <ReceiptSMSForm
          :object-id="transaction.id"
          receipt-type="transaction"
          @submit-success="emit('close')"
        />
      </div>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import EmailReceiptIcon from "~icons/heroicons/at-symbol"
import SMSReceiptIcon from "~icons/heroicons/chat-bubble-bottom-center-text"
import PrintReceiptIcon from "~icons/heroicons/printer"
import type { Transaction } from "@/services/transactions/listTransactions"

import type {
  Refund,
  TransactionDetail,
} from "@/services/sharedTypes/transactions"
import { pascalize } from "humps"

const props = defineProps<{
  transaction: TransactionDetail | Refund | Transaction
  isOpen: boolean
}>()

const { organizationConfig } = useOrganizationConfig()

watch(
  () => props.isOpen,
  (value) => {
    if (value === true) {
      step.value = "initial"
    }
  }
)

const emit = defineEmits(["close"])

const step = ref<
  "initial" | "refund" | "emailReceipt" | "smsReceipt"
>("initial")
const isLoading = ref(false)

watch(
  () => props.isOpen,
  (newValue, oldValue) => {
    if (newValue && !oldValue) {
      step.value = "initial"
    }
  }
)

const typeLabel = computed(
  () =>
    ({
      payment: "Payment",
      refund: "Refund",
    })[props.transaction.type]
)

const statusLabel = computed(() =>
  pascalize(props.transaction.status)
)

const onDownloadMerchantReceipt = async () => {
  if (!props.transaction) return
  const { receipt } = await retrieveTransactionReceipt(
    props.transaction.id
  )
  download(
    receipt,
    `Transaction: ${props.transaction.id} receipt (merchant).txt`,
    "text/plain"
  )
}
</script>
